import { useQuery } from '@apollo/client';
import { useState, useContext, useEffect, useMemo, useCallback } from 'react';

import {
  AnalyticsModifiersContext,
  BookValuesContext,
  CollaborationModifiersContext,
  CollaborationValuesContext,
  NetworkStatusValuesContext,
  UserContentValuesContext,
  UserSessionModifiersContext,
  UserSessionValuesContext,
} from 'contexts';
import {
  useDeleteUserContentShareMutation,
  useUpdateUserContentShareMutation,
  useCreateUserContentShareSubscriptionMutation,
  useDeleteUserContentShareSubscriptionMutation,
  useUpdateUserContentShareSubscriptionMutation,
} from 'hooks';
import COLLABORATION_SHARES_AND_SUBSCRIPTIONS from 'graphql/queries/CollaborationSharesAndSubscriptionsQuery.graphql';
import { User, UserContentShare, UserContentShareSubscription } from 'types';
import { getIDFromPK } from 'utilities';
import { USER_CONTENT_REFETCH } from 'constants';

export const CollaborationProvider = ({ children }) => {
  const analytics = useContext(AnalyticsModifiersContext);
  const { book } = useContext(BookValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const userContent = useContext(UserContentValuesContext);
  const { user } = useContext(UserSessionValuesContext);
  const { addOtherUser } = useContext(UserSessionModifiersContext);

  const [deleteUserContentShareMutation] = useDeleteUserContentShareMutation();
  const [updateUserContentShareMutation] = useUpdateUserContentShareMutation();
  const [createUserContentShareSubscriptionMutation] =
    useCreateUserContentShareSubscriptionMutation();
  const [deleteUserContentShareSubscriptionMutation] =
    useDeleteUserContentShareSubscriptionMutation();
  const [updateUserContentShareSubscriptionMutation] =
    useUpdateUserContentShareSubscriptionMutation();

  const [share, setShare] = useState();
  const [subscriptions, setSubscriptions] = useState([]);

  const { error, data, refetch } = useQuery(
    COLLABORATION_SHARES_AND_SUBSCRIPTIONS,
    {
      variables: { epubPk: book.pk },
      skip: isOffline,
    },
  );

  /**
   * Main useEffect to parse the user's share information as well as their subscriptions.
   */
  useEffect(() => {
    if (!data || error) {
      return;
    }

    // Parse the current user's subscriptions
    const subscriptions = [];
    for (let { node } of data.userContentShareSubscriptionsByEpub.edges) {
      const {
        id,
        isPaused,
        share: {
          sharerId,
          sharerFirstName: firstName,
          sharerLastName: lastName,
          code,
        },
      } = node;
      const sharer = addOtherUser(
        new User({
          id: getIDFromPK(sharerId, 'PlatformUserNode'),
          firstName,
          lastName,
        }),
      );

      subscriptions.push(
        new UserContentShareSubscription({
          id,
          sharer,
          isPaused,
          code,
        }),
      );
    }

    setSubscriptions(subscriptions);

    // Parse the current user's share
    const {
      userContentShareByEpub: { id, isPaused, code: sharingCode },
    } = data;
    setShare(
      new UserContentShare({
        id,
        isPaused,
        sharer: user,
        sharingCode,
      }),
    );
  }, [addOtherUser, data, error, user]);

  /**
   * Creates a new share, with a new sharing code,
   * while invalidating the user's existing share and all associated subscriptions.
   *
   * @method generateNewSharingCode
   */
  const generateNewSharingCode = useCallback(async () => {
    const {
      data: {
        deleteUserContentShare: {
          userContentShare: { code: sharingCode, id, isPaused },
        },
      },
    } = await deleteUserContentShareMutation({
      variables: {
        epubPk: book.pk,
      },
    });

    // We will get the new code with the new share record,
    // which is returned when we delete the existing one
    setShare(
      new UserContentShare({
        id,
        isPaused,
        sharer: user,
        sharingCode,
      }),
    );
  }, [book.pk, deleteUserContentShareMutation, user]);

  /**
   * Adds a subscription using another user's sharing code.
   *
   * @method addSubscription
   * @param {String} sharingCode a string that corresponds to another user's share
   */
  const addSubscription = useCallback(
    async (sharingCode) => {
      if (!sharingCode) {
        return false;
      }

      const {
        data: { createUserContentShareSubscription: success },
      } = await createUserContentShareSubscriptionMutation({
        variables: {
          code: sharingCode,
          epubPk: book.pk,
        },
      });

      // Condition where the code did not unlock a share
      if (!success) {
        return false;
      }

      await refetch();
      await userContent.refetch?.([
        USER_CONTENT_REFETCH.ANNOTATIONS_FROM_SHARES_IN_DOCUMENT,
        USER_CONTENT_REFETCH.ANNOTATIONS_FROM_SHARES_NOT_IN_DOCUMENT,
      ]);

      analytics.collaborationNoteSubscribed();

      return true;
    },
    [
      analytics,
      book,
      createUserContentShareSubscriptionMutation,
      refetch,
      userContent,
    ],
  );

  /**
   * Removes a subscription.
   *
   * @method removeSubscription
   * @param {UserContentShareSubscription} subscription a subscription instance
   */
  const removeSubscription = useCallback(
    async (subscription) => {
      await deleteUserContentShareSubscriptionMutation({
        variables: {
          userContentShareSubscriptionPk: subscription.pk,
        },
      });

      await refetch();
      await userContent.refetch?.([
        USER_CONTENT_REFETCH.ANNOTATIONS_FROM_SHARES_IN_DOCUMENT,
        USER_CONTENT_REFETCH.ANNOTATIONS_FROM_SHARES_NOT_IN_DOCUMENT,
      ]);
    },
    [deleteUserContentShareSubscriptionMutation, refetch, userContent],
  );

  /**
   * Helper to call a mutation to update the user's share.
   *
   * @method updateShare
   * @param {Object} properties properties to use to update the share
   */
  const updateShare = useCallback(
    async (properties) => {
      const {
        data: {
          updateUserContentShare: {
            userContentShare: { id, isPaused, code: sharingCode },
          },
        },
      } = await updateUserContentShareMutation({
        variables: {
          epubPk: book.pk,
          ...properties,
        },
      });

      setShare(
        new UserContentShare({
          id,
          isPaused,
          sharer: user,
          sharingCode,
        }),
      );
    },
    [book.pk, updateUserContentShareMutation, user],
  );

  /**
   * Pauses sharing if sharing is not already paused.
   *
   * @method pauseShare
   */
  const pauseShare = useCallback(async () => {
    if (!share.isPaused) {
      await updateShare({ isPaused: true });
    }
  }, [share, updateShare]);

  /**
   * Resumes sharing if sharing is paused.
   *
   * @method pauseShare
   */
  const resumeShare = useCallback(async () => {
    if (share.isPaused) {
      await updateShare({ isPaused: false });
    }
  }, [share, updateShare]);

  /**
   * Helper to call a mutation to update a subscription.
   *
   * @method updateSubscription
   * @param {UserContentShareSubscription} subscription a subscription instance
   * @param {Object} properties properties to use to update the instance
   */
  const updateSubscription = useCallback(
    async (subscription, properties) => {
      await updateUserContentShareSubscriptionMutation({
        variables: {
          subscriptionId: subscription.id,
          ...properties,
        },
      });

      await refetch();
      await userContent.refetch?.([
        USER_CONTENT_REFETCH.ANNOTATIONS_FROM_SHARES_IN_DOCUMENT,
        USER_CONTENT_REFETCH.ANNOTATIONS_FROM_SHARES_NOT_IN_DOCUMENT,
      ]);
    },
    [refetch, updateUserContentShareSubscriptionMutation, userContent],
  );

  /**
   * Pauses an unpaused subscription.
   *
   * @method pauseSubscription
   * @param {UserContentShareSubscription} subscription a subscription instance
   */
  const pauseSubscription = useCallback(
    (subscription) =>
      !subscription.isPaused &&
      updateSubscription(subscription, { isPaused: true }),
    [updateSubscription],
  );

  /**
   * Resumes a paused subscription.
   *
   * @method resumeSubscription
   * @param {UserContentShareSubscription} subscription a subscription instance
   */
  const resumeSubscription = useCallback(
    (subscription) =>
      subscription.isPaused &&
      updateSubscription(subscription, { isPaused: false }),
    [updateSubscription],
  );

  const values = useMemo(
    () => ({ share, subscriptions }),
    [share, subscriptions],
  );

  const modifiers = useMemo(
    () => ({
      generateNewSharingCode,
      pauseShare,
      resumeShare,
      pauseSubscription,
      resumeSubscription,
      addSubscription,
      removeSubscription,
    }),
    [
      generateNewSharingCode,
      pauseShare,
      resumeShare,
      pauseSubscription,
      resumeSubscription,
      addSubscription,
      removeSubscription,
    ],
  );

  // For debug
  window.v7.collaboration = {
    ...values,
    ...modifiers,
  };

  return (
    <CollaborationValuesContext.Provider value={values}>
      <CollaborationModifiersContext.Provider value={modifiers}>
        {children}
      </CollaborationModifiersContext.Provider>
    </CollaborationValuesContext.Provider>
  );
};

export default CollaborationProvider;
